import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { formatPrice } from '../config/currency';
import { getImageUrl } from '../config/imageUrl';
import { orderService } from '../services/order.service';
import { OrderListItem, OrderResponse } from '../types/order.types';
import { OrderDetailsModal } from './Orders/OrderDetailsModal';

export const OrdersPage: React.FC = () => {
  const { t, language } = useLanguage();
  const { error: showError, success: showSuccess } = useNotification();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<OrderResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Загрузка заказов
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getOrders();
      setOrders(data);
    } catch (err) {
      console.error('Failed to load orders:', err);
      showError(t('orders.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Открытие модалки с деталями
  const handleViewOrder = async (orderId: number) => {
    try {
      setLoadingDetails(true);
      const order = await orderService.getOrder(orderId);
      setSelectedOrder(order);
      setIsModalOpen(true);
    } catch (err) {
      console.error('Failed to load order details:', err);
      showError(t('orders.errorLoading'));
    } finally {
      setLoadingDetails(false);
    }
  };

  // Удаление заказа
  const handleDeleteOrder = async (orderId: number) => {
    if (!window.confirm(t('orders.confirmDelete'))) return;

    try {
      await orderService.deleteOrder(orderId);
      showSuccess(t('orders.deleteSuccess'));
      loadOrders();
    } catch (err) {
      console.error('Failed to delete order:', err);
      showError(t('orders.deleteError'));
    }
  };

  // Форматирование даты
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(language === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Название источника
  const getSourceName = (order: OrderListItem) => {
    return language === 'ru' ? order.source_info.name_ru : order.source_info.name_en;
  };

  // Получить номер заказа из деталей (если есть)
  const getOrderNumberFromDetails = (order: OrderListItem) => {
    const formDataEntries = Object.entries(order.form_data);
    for (const [, field] of formDataEntries) {
      const label = language === 'ru' ? field.label_ru : field.label_en;
      if (label.toLowerCase().includes('номер') || label.toLowerCase().includes('number')) {
        return field.value;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="container-app py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-text-secondary">{t('orders.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-app py-8">
      {/* Заголовок */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary mb-2">{t('orders.title')}</h1>
        <p className="text-text-secondary">{t('orders.subtitle')}</p>
      </div>

      {/* Кнопка создания заказа */}
      <div className="mb-6 flex justify-end">
        <button className="btn btn-primary" onClick={() => navigate('/orders/add')}>
          {t('orders.createNew')}
        </button>
      </div>

      {/* Список заказов */}
      <div className="space-y-4">
        {orders.length === 0 ? (
          <div className="card text-center py-12 text-text-secondary">
            {t('orders.noOrders')}
          </div>
        ) : (
          orders.map((order) => {
            const orderNumberFromDetails = getOrderNumberFromDetails(order);
            return (
            <div key={order.id} className="card hover:shadow-md transition-shadow">
              <div className="flex flex-col gap-4">
                {/* Шапка заказа */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <h3 className="text-lg font-semibold text-text-primary">
                    {t('orders.orderNumber')} #{order.id}
                    {orderNumberFromDetails && (
                      <span className="text-text-secondary"> ({orderNumberFromDetails})</span>
                    )}
                  </h3>
                  <div className="text-sm text-text-secondary">
                    {formatDate(order.created_at)}
                  </div>
                </div>

                {/* Источник */}
                <div className="text-sm text-text-secondary">
                  {t('orders.source')}: <span className="text-text-primary">{getSourceName(order)}</span>
                </div>

                {/* Позиции заказа с картинками */}
                <div className="flex flex-wrap gap-3">
                  {order.items.slice(0, 5).map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 bg-surface-secondary rounded-lg p-2"
                    >
                      {getImageUrl(item.dish_image) ? (
                        <img
                          src={getImageUrl(item.dish_image)!}
                          alt={item.dish_name}
                          className="w-10 h-10 rounded-md object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-surface-tertiary flex items-center justify-center">
                          <span className="text-text-tertiary text-xs">🍽️</span>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm text-text-primary font-medium truncate max-w-[120px]">
                          {item.dish_name}
                        </span>
                        <span className="text-xs text-text-secondary">
                          {item.weight}{t('addOrder.grams')} × {item.quantity}
                        </span>
                      </div>
                    </div>
                  ))}
                  {order.items.length > 5 && (
                    <div className="flex items-center justify-center bg-surface-secondary rounded-lg px-3 py-2">
                      <span className="text-sm text-text-secondary">
                        +{order.items.length - 5}
                      </span>
                    </div>
                  )}
                </div>

                {/* Итого и действия */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-border-primary">
                  <div className="text-lg font-bold text-text-primary">
                    {t('orders.total')}: {formatPrice(order.total)}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleViewOrder(order.id)}
                      disabled={loadingDetails}
                    >
                      {t('orders.actionView')}
                    </button>
                    <button
                      className="btn btn-error btn-sm"
                      onClick={() => handleDeleteOrder(order.id)}
                    >
                      {t('orders.actionDelete')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* Модалка деталей заказа */}
      {selectedOrder && (
        <OrderDetailsModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedOrder(null);
          }}
          order={selectedOrder}
        />
      )}
    </div>
  );
};
